// @ts-check
import {
  localAnestheticLookupById,
  getLocalAnestheticVariantsForCountry,
} from '../local-anesthetics'
import { convertLa } from '../utils/convert-la'
import { roundToDecimal } from '../utils/decimals'
import { diluteLocalsRatio } from '../utils/dilute-locals-ratio'
import { createDoseProp } from '../utils/dose-prop'
import { formatEpi } from '../utils/format'
import { createId } from '../utils/id'
import {
  removeKeysWithNullishValues,
  replaceObjectRawKeys,
} from '../utils/object'
import { createSelector } from '../utils/selector-utils'
import { sortByTimeIfExists } from '../utils/sort-by'
import { selectLocalDosesAdministeredRawById } from './drugs-administered'
import {
  actionAddLocalDilutionRaw,
  actionRemoveLocalDilutionRaw,
  selectLocalDilutionsByBaseDrugId,
} from './la-dilutions'

const extractRxcui = id => {
  const splitId = id.split('|')
  if (splitId.length == 2 && /^\d+$/.test(splitId[0])) {
    return splitId[0]
  }
  return null
}
const prop = createDoseProp({
  route: 'local',
  drugInfoProp: 'localAnesthetics',
  mapFn: drug => {
    if (drug.rxcui || !drug.id) {
      return drug
    }
    const rxcui = extractRxcui(drug.id)
    if (rxcui) {
      return { ...drug, rxcui }
    }
    return drug
  },
})

/**
 * @param {{
 *   id: string
 *   rxcui?: string
 *   name: string
 *   epi: number
 *   percent: number
 * }} localDrug
 *   The drug we're selecting. We really want rxcui to be required, but we may not
 *   have it if we're creating it off of presets.
 * @returns {any} The changes to apply to the case.
 */
export const actionSelectLocalDrug = ({ id, rxcui, name, epi, percent }) =>
  prop.actionSelect(id, { name, epi, percent, rxcui })

/**
 * @param {string} id The rxcui ID of the drug we're unselecting
 * @returns {any} The changes to apply to the case.
 */
export const actionUnselectLocalDrug = prop.actionUnselect

/**
 * @param {string} id
 * @param {{
 *   unit: LocalDoseUnit
 *   amount_raw: string
 * }} updates
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateLocalDosage = prop.actionUpdateDosage

/**
 * @param {string} id
 * @param {LocalDoseUnit} unit
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateLocalUnit = (id, unit) =>
  prop.actionUpdateDosage(id, { unit })

/**
 * @param {string} id
 * @param {string} amountRaw
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateLocalAmount = (id, amountRaw) =>
  prop.actionUpdateDosage(id, { amount_raw: amountRaw })

export const actionAddLocalDilution = (drugVariantId, percentStrength) => {
  const variant = localAnestheticLookupById[drugVariantId]
  if (!variant || !variant.enableDilute) return
  const { name, epi, percent, rxcui } = variant
  const id = createId('customla')
  return {
    ...actionAddLocalDilutionRaw(
      {
        variantId: drugVariantId,
        percent: percentStrength,
      },
      id
    ),
    ...actionSelectLocalDrug({ id, name, epi, percent, rxcui }),
  }
}

/**
 * @param {string} customVariantId
 * @returns {any} The changes to apply to the case.
 */
export const actionRemoveLocalDilution = customVariantId => ({
  ...actionRemoveLocalDilutionRaw(customVariantId),
  ...actionUnselectLocalDrug(customVariantId),
})

/**
 * @param {MacroLocalDrug} localDrugSelection
 * @returns {any} The changes to apply to the case.
 */
export const actionPresetLocalDrug = ({
  id,
  name,
  percent,
  epi,
  unit,
  dose,
}) => ({
  ...actionSelectLocalDrug({ id, name, percent, epi }),
  ...actionUpdateLocalUnit(id, unit),
  ...(dose ? actionUpdateLocalAmount(id, dose) : {}),
})

export const getSubName = variant => {
  if (variant.subName) {
    return variant.subName
  }
  if (variant.epi != null) {
    if (variant.epi > 0) {
      return formatEpi(variant.epi)
    }
    return '(no epi)'
  }
}

/** @type {CaseSelector<LocalAnestheticVariant[]>} */
export const selectLocalDrugVariantsFullList = createSelector(
  selectLocalDosesAdministeredRawById,
  selectLocalDilutionsByBaseDrugId,
  prop.selectDosage,
  /**
   * @param selectedById
   * @param {{
   *   [drugVariantId: string]: LocalDilutionEntry[]
   * }} dilutionsByBaseDrugId
   * @returns
   */
  (dosesById, dilutionsByBaseDrugId, dosageById) => {
    // in the future this country could come from case data
    const baseList = getLocalAnestheticVariantsForCountry('US')
    /** @type {LocalAnestheticVariant[]} */
    const result = []
    const addVariant = variant => {
      result.push({
        ...variant,
        selected: !!dosageById[variant.id],
        subName: getSubName(variant),
      })
    }

    baseList.forEach(variant => {
      addVariant(variant)
      const dilutions = dilutionsByBaseDrugId[variant.id]
      if (dilutions) {
        dilutions.forEach(customVariant => {
          const { percent, id } = customVariant
          addVariant({
            ...variant,
            id,
            custom: true,
            percent: variant.percent * percent,
            epi: diluteLocalsRatio(variant.epi, percent),
          })
        })
      }
    })

    return result.map(variant => {
      let doses

      if (variant.selected) {
        doses = (dosesById[variant.id] || []).map(dose => {
          const {
            id,
            unit,
            amount,
            amount_raw,
            epiConcentration,
            drugVariantId,
            shouldStartCase,
            time,
          } = dose

          let { mg, ml, cartridges } = convertLa({
            [unit]: amount,
            drugPercent: variant.percent,
          })

          let epiMg = 0
          if (epiConcentration) {
            const epiMgPerMl = 1 / epiConcentration
            epiMg = roundToDecimal(ml * epiMgPerMl, 3)
          }
          /** @type {LocalDoseProcessed} */
          const toReturn = {
            id,
            drugVariantId,
            time,
            amount,
            amount_raw,
            unit,
            epiConcentration,
            cartridges,
            epiMg,
            mg,
            shouldStartCase,
            skipForToxicity: !!variant.skipForToxicity,
          }
          return toReturn
        })
      } else {
        doses = []
      }

      const totalMgGiven = roundToDecimal(
        doses.reduce((total, dose) => total + dose.mg, 0),
        2
      )
      const totalCartridgesGiven = roundToDecimal(
        doses.reduce((total, dose) => total + dose.cartridges, 0),
        2
      )
      const totalEpiMgGiven = roundToDecimal(
        doses.reduce((total, dose) => total + dose.epiMg, 0),
        2
      )

      const { amount, amount_raw, unit } = replaceObjectRawKeys(
        dosageById[variant.id] || {}
      )

      let nameWithPct = variant.name
      const percent = variant.percent

      if (percent) {
        nameWithPct = `${roundToDecimal(percent * 100, 2)}% ${nameWithPct}`
      }

      return removeKeysWithNullishValues({
        ...variant,
        nameWithPct,
        amount,
        amount_raw,
        unit,
        doses,
        inUse: !!doses.length,
        totalMgGiven,
        totalCartridgesGiven,
        totalEpiMgGiven,
      })
    })
  }
)

/** @type {CaseSelector<Record<string, LocalAnestheticVariant>>} */
export const selectLocalDrugVariantsFullListById = createSelector(
  selectLocalDrugVariantsFullList,
  variants =>
    variants.reduce((acc, variant) => {
      acc[variant.id] = variant
      return acc
    }, {})
)

/** @type {CaseSelector<LocalAnestheticVariant[]>} */
export const selectSelectedLocals = createSelector(
  selectLocalDrugVariantsFullList,
  /** @param {LocalAnestheticVariant[]} variants */
  variants => variants.filter(item => item.selected)
)

/** @type {CaseSelector<Record<string, LocalAnestheticVariant>>} */
export const selectSelectedLocalsById = createSelector(
  selectSelectedLocals,
  selected =>
    selected.reduce((acc, item) => {
      acc[item.id] = item
      return acc
    }, {})
)

/**
 * We re-assemble it from here instead of doing it at a lower level because with
 * this approach we can keep add in all the context of the drug variant, such as
 * whether or not it should be included in toxicity calculations.
 *
 * This is bit round-about but it should work well and since we don't expect
 * anything in the selector layers below to rapidly it should not have any
 * significant negative performance implications.
 *
 * @type {CaseSelector<LocalDoseProcessed[]>}
 */
export const selectLocalDosesAdministered = createSelector(
  selectSelectedLocals,
  /** @param {LocalAnestheticVariant[]} variants */
  variants => {
    /** @type {LocalAnestheticVariant['doses']} */
    const toReturn = []
    variants.forEach(variant => {
      toReturn.push(...variant.doses)
    })
    toReturn.sort(sortByTimeIfExists)
    return toReturn
  }
)

/** @type {CaseSelector<boolean>} */
export const selectHasUsedNonToxicLocal = createSelector(
  selectLocalDosesAdministered,
  doses => doses.some(dose => dose.skipForToxicity)
)

/** @type {CaseSelector<boolean>} */
export const selectHasTrackedLocalAnesthetics = createSelector(
  selectLocalDosesAdministered,
  /** @param {LocalAnestheticVariant['doses']} doses */
  doses => doses.some(dose => dose.mg > 0)
)

/** @type {CaseSelector<MacroLocalDrug[]>} */
export const selectLocalsForMacro = createSelector(
  selectSelectedLocals,
  prop.selectDosageRaw,
  (locals, dosage) =>
    locals.map(({ id, rxcui, name, percent, epi, doses }) => {
      const { amount_raw, unit } = dosage[id] || {}
      const firstDose = doses[0]
      return removeKeysWithNullishValues(
        {
          id,
          rxcui,
          name,
          percent,
          epi,
          dose: (firstDose && firstDose.amount_raw) || amount_raw,
          unit: (firstDose && firstDose.unit) || unit,
        },
        true
      )
    })
)
